import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, takeUntil } from 'rxjs';
import { environment } from '@deltager/organizers/admin/environments/environment';
import { map } from 'rxjs/operators';
import { RootState, selectOrganizer } from '@deltager/organizers/admin/redux/root.state';
import { Store } from '@ngrx/store';
import { BaseComponent } from '@deltager/organizers/admin/shared/components/base.component';
import {
  BookingPageListItem,
  BookingPageParams,
  BookingReportCalendarFilter,
  BookingReportTableFilter,
} from './booking-report-filters/booking-report-filters.models';
import { PagedList } from '../../shared/models/paged-list.model';
import { BookingReportTableItem } from './booking-report-table/booking-report-table.models';
import { BookingReportCalendarItem } from '@deltager/organizers/admin/modules/booking-report/booking-report-calendar/booking-report-calendar.models';
import { DayDate } from '../../core/date-format/dayDate';
import { numberToColor } from './booking-report.utils';
import { UserBookingReportPredefined } from './models/user-booking-report-predefined.model';

@Injectable({
  providedIn: 'root',
})
export class BookingReportApiService extends BaseComponent {
  public organiserId: number;

  constructor(
    private http: HttpClient,
    private store: Store<RootState>,
  ) {
    super();
    this.store
      .select(selectOrganizer)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(organizer => {
        this.organiserId = organizer?.id;
      });
  }

  getBookingPages(
    pageParams: BookingPageParams = { pageNumber: 0, pageSize: 5, excludeIds: [] },
  ): Observable<BookingPageListItem[]> {
    let params = new HttpParams().set('pageNumber', pageParams.pageNumber).set('pageSize', pageParams.pageSize);

    if (pageParams.search) {
      params = params.set('search', pageParams.search);
    }

    if (pageParams.excludeIds.length > 0) {
      pageParams.excludeIds.forEach(id => {
        params = params.append('excludeIds', id);
      });
    }

    return this.http.get<BookingPageListItem[]>(`${this.baseUrl()}/${this.organiserId}/events`, { params }).pipe(
      map(data => {
        data.forEach(itm => (itm.color = numberToColor(itm.id)));
        return data;
      }),
    );
  }

  public loadCalendarItems(filters: BookingReportCalendarFilter): Observable<BookingReportCalendarItem[]> {
    let params = new HttpParams()
      .set('startDate', DayDate.toIsoWithoutTimezone(filters.startDate, true))
      .set('endDate', DayDate.toIsoWithoutTimezone(filters.endDate, true));

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.bookingEventIds?.length > 0) {
      filters.bookingEventIds.forEach(id => {
        params = params.append('bookingEventIds', id);
      });
    }

    return this.http.get<BookingReportCalendarItem[]>(`${this.baseUrl()}/${this.organiserId}/calendar`, { params });
  }

  loadTableItems(filters: BookingReportTableFilter): Observable<PagedList<BookingReportTableItem>> {
    let params = new HttpParams()
      .set('pageNumber', filters.pageNumber ?? 0)
      .set('pageSize', filters.pageSize ?? BookingReportTableFilter.DefaultPageSize);

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.bookingEventIds?.length > 0) {
      filters.bookingEventIds.forEach(id => {
        params = params.append('bookingEventIds', id);
      });
    }

    if (filters.startDate && filters.endDate) {
      params = params.set('startDate', DayDate.toIsoWithoutTimezone(filters.startDate, true));
      params = params.set('endDate', DayDate.toIsoWithoutTimezone(filters.endDate, true));
    }

    if (filters.startTime && filters.endTime) {
      params = params.set('startTime', DayDate.toTimeFormat(new Date(filters.startTime)));
      params = params.set('endTime', DayDate.toTimeFormat(new Date(filters.endTime)));
    }

    return this.http.get<PagedList<BookingReportTableItem>>(`${this.baseUrl()}/${this.organiserId}/table`, { params });
  }

  getUserBookingReportPredefined(): Observable<UserBookingReportPredefined> {
    return this.http.get<UserBookingReportPredefined>(
      `${environment.apiBaseURL}/user/organizers/${this.organiserId}/booking-report-predefined`,
    );
  }

  updateUserBookingReportPredefined(data: UserBookingReportPredefined): Observable<any> {
    return this.http.put(
      `${environment.apiBaseURL}/user/organizers/${this.organiserId}/booking-report-predefined`,
      data,
    );
  }

  private baseUrl(): string {
    return `${environment.apiBaseURL}/booking-report`;
  }
}
